import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Camera, Mic, Volume2, Wifi, Video, Radio, ShoppingBag, UserPlus, ShieldCheck, Users,
  CheckCircle2, XCircle, AlertTriangle, Sparkles, Save, Trash2, ArrowUp, ArrowDown, X, Plus,
  Gauge, Sun, Play, Ban, Globe, Clock, Loader2, Gavel, Clapperboard } from "lucide-react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const LANGS = [["en", "English"], ["fr", "Français"], ["ht", "Kreyòl"], ["es", "Español"]];
const CATS = ["Fashion", "Beauty", "Home", "Electronics", "Education", "TAVEN Signature", "Best Sellers"];

// Safely turn any FastAPI error (string OR 422 detail array) into a display string.
const errMsg = (e, fallback = "Something went wrong.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join(", ");
  if (d && typeof d === "object") return d.error || fallback;
  return fallback;
};

const Check = ({ ok, warn, label, detail, onOverride, overridden, testid }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-white/10" data-testid={testid}>
    {ok ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" /> : overridden ? <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" /> : warn ? <Loader2 className="w-5 h-5 text-white/40 shrink-0 animate-spin" /> : <XCircle className="w-5 h-5 text-red-400 shrink-0" />}
    <div className="flex-1 min-w-0"><p className="text-sm font-bold">{label}</p>{detail && <p className="text-[11px] text-white/50 truncate">{detail}</p>}</div>
    {!ok && onOverride && <button onClick={onOverride} data-testid={`${testid}-override`} className={`text-[10px] font-bold uppercase px-2 py-1 border ${overridden ? "border-amber-400 text-amber-400" : "border-white/20 text-white/60 hover:border-white/50"}`}>{overridden ? "Overridden" : "Override"}</button>}
  </div>
);

export default function GreenRoom() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const { user, authApi } = useAuth();
  const [session, setSession] = useState(null);
  const [cfg, setCfg] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const [devices, setDevices] = useState({ cams: [], mics: [], spks: [] });
  const [sel, setSel] = useState({ cam: "", mic: "", spk: "" });
  const [camOk, setCamOk] = useState(false);
  const [micOk, setMicOk] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [quality, setQuality] = useState("");
  const [lighting, setLighting] = useState(null);
  const [net, setNet] = useState({ status: "testing", ms: null });
  const [fx, setFx] = useState({ blur: false, noise: true, echo: true });
  const [form, setForm] = useState({ title: "", description: "", category: "", language: "en", cover_image: "" });
  const [products, setProducts] = useState([]);
  const [roster, setRoster] = useState({ guests: [], moderators: [], cohosts: [] });
  const [overrides, setOverrides] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [starting, setStarting] = useState(false);
  const saveTimer = useRef(null);

  // ---- load session + config ----
  useEffect(() => {
    api.get("/live/greenroom/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get(`/live/sessions/${sessionId}`).then(({ data }) => {
      setSession(data);
      setForm({ title: data.title || "", description: data.description || "", category: data.category || "", language: data.language || "en", cover_image: data.cover_image || "" });
      setProducts(data.products || []);
      setRoster({ guests: data.guests || [], moderators: data.moderators || [], cohosts: data.cohosts || [] });
      setOverrides((data.greenroom || {}).overrides || []);
    }).catch(() => toast.error("Could not load session."));
  }, [sessionId]);

  // ---- device preview ----
  const startPreview = useCallback(async () => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: sel.cam ? { deviceId: { exact: sel.cam } } : true,
        audio: { deviceId: sel.mic ? { exact: sel.mic } : undefined, echoCancellation: fx.echo, noiseSuppression: fx.noise },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      const vt = stream.getVideoTracks()[0];
      setCamOk(!!vt);
      if (vt) { const s = vt.getSettings(); setQuality(`${s.width || "?"}×${s.height || "?"} @ ${Math.round(s.frameRate || 0)}fps`); }
      const at = stream.getAudioTracks()[0];
      setMicOk(!!at);
      // mic level meter
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser(); an.fftSize = 256; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => { an.getByteFrequencyData(buf); setMicLevel(Math.min(100, Math.round(buf.reduce((a, b) => a + b, 0) / buf.length / 1.2))); rafRef.current = requestAnimationFrame(tick); };
      tick();
      // enumerate after permission (labels populate)
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cams: all.filter((d) => d.kind === "videoinput"),
        mics: all.filter((d) => d.kind === "audioinput"),
        spks: all.filter((d) => d.kind === "audiooutput"),
      });
    } catch (e) {
      setCamOk(false); setMicOk(false);
      toast.error("Camera/mic unavailable — grant permission or override to continue.");
    }
  }, [sel.cam, sel.mic, fx.echo, fx.noise]);

  useEffect(() => { startPreview(); return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); cancelAnimationFrame(rafRef.current); audioCtxRef.current?.close().catch(() => {}); }; }, [startPreview]);

  // lighting check (avg luminance from a video frame)
  useEffect(() => {
    if (!camOk) return;
    const id = setInterval(() => {
      try {
        const v = videoRef.current; if (!v || !v.videoWidth) return;
        const c = document.createElement("canvas"); c.width = 64; c.height = 36;
        const g = c.getContext("2d"); g.drawImage(v, 0, 0, 64, 36);
        const d = g.getImageData(0, 0, 64, 36).data; let sum = 0;
        for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        const avg = sum / (d.length / 4);
        setLighting(avg < 55 ? "low" : avg > 205 ? "high" : "good");
      } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, [camOk]);

  // network + latency test
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const samples = [];
      for (let i = 0; i < 4; i++) {
        const t0 = performance.now();
        try { await api.get("/health"); samples.push(performance.now() - t0); } catch {}
        await new Promise((r) => setTimeout(r, 250));
      }
      if (cancelled) return;
      if (!samples.length) { setNet({ status: "poor", ms: null }); return; }
      const ms = Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
      setNet({ status: ms < 150 ? "excellent" : ms < 400 ? "good" : "poor", ms });
    })();
    return () => { cancelled = true; };
  }, []);

  // speaker output selection (setSinkId)
  useEffect(() => { if (sel.spk && videoRef.current?.setSinkId) videoRef.current.setSinkId(sel.spk).catch(() => {}); }, [sel.spk]);
  const testSpeaker = async () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 660; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination); o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, 350);
    } catch { toast.error("Could not play test sound."); }
  };

  // ---- autosave (debounced) ----
  const readiness = {
    camera: camOk, microphone: micOk, network: net.status !== "poor" && net.status !== "testing",
    title: !!form.title.trim(),
  };
  const isReady = ["camera", "microphone", "network", "title"].every((k) => readiness[k] || overrides.includes(k));

  const persist = useCallback((ready) => {
    authApi.put(`/live/sessions/${sessionId}/greenroom`, {
      ...form, checklist: readiness, overrides, ready,
      product_order: products.map((p) => p.handle),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, readiness, overrides, products, sessionId]);

  useEffect(() => {
    if (!session) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(false), 900);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, overrides, products, camOk, micOk, net.status]);

  const toggleOverride = (k) => setOverrides((o) => o.includes(k) ? o.filter((x) => x !== k) : [...o, k]);

  // ---- roster ----
  const addRoster = async (role, email) => {
    if (!email) return;
    try { const { data } = await authApi.post(`/live/sessions/${sessionId}/roster`, { email, role }); setRoster(data); toast.success(`${role} added`); }
    catch (e) { toast.error(errMsg(e, "Could not add — check the email address.")); }
  };
  const removeRoster = async (role, email) => {
    try { const { data } = await authApi.delete(`/live/sessions/${sessionId}/roster`, { params: { email, role } }); setRoster(data); }
    catch {}
  };

  // ---- products ----
  const openPicker = async () => { setShowPicker(true); if (!catalog.length) { try { const { data } = await api.get("/shop/products"); setCatalog((Array.isArray(data) ? data : data.products || []).slice(0, 40)); } catch {} } };
  const addProduct = async (handle) => { try { const { data } = await authApi.put(`/live/sessions/${sessionId}/products/add`, { handle }); setProducts(data.products || []); toast.success("Added to queue"); } catch (e) { toast.error(errMsg(e, "Could not add product.")); } };
  const removeProduct = async (handle) => { try { const { data } = await authApi.put(`/live/sessions/${sessionId}/products/remove`, { handle }); setProducts(data.products || []); } catch {} };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= products.length) return; const arr = [...products]; [arr[i], arr[j]] = [arr[j], arr[i]]; setProducts(arr); };

  // ---- start / cancel ----
  const startLive = async () => {
    if (!isReady) { toast.error("Complete or override the required checks first."); return; }
    setStarting(true);
    try { await persist(true); await new Promise((r) => setTimeout(r, 300)); nav(`/live/${sessionId}`); }
    finally { setStarting(false); }
  };
  const cancel = async () => { try { await authApi.post(`/live/sessions/${sessionId}/cancel`); toast.success("Session cancelled."); nav("/live"); } catch { toast.error("Could not cancel."); } };

  if (!user) return <div className="py-24 text-center bg-taven-black min-h-screen text-white"><p className="mb-3">Sign in to open the Green Room.</p><Link to="/login" className="bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider">Sign in</Link></div>;
  if (cfg && !cfg.enabled) return <div className="py-24 text-center bg-taven-black min-h-screen text-white">Green Room is not enabled in this environment.</div>;
  if (!session) return <div className="py-24 text-center bg-taven-black min-h-screen text-white">Loading Green Room…</div>;

  const netLabel = { testing: "Testing…", excellent: "Excellent", good: "Good", poor: "Weak" }[net.status];

  return (
    <div className="bg-taven-black min-h-screen text-white" data-testid="green-room">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black font-display flex items-center gap-2"><Radio className="w-6 h-6 text-taven-gold" /> Green Room</h1>
            <p className="text-sm text-white/50">Prepare everything before you go live. Nothing is broadcast until you press <strong>Go Live</strong>.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cancel} data-testid="greenroom-cancel" className="flex items-center gap-1.5 border border-white/20 text-white/70 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-red-400 hover:text-red-400"><Ban className="w-4 h-4" /> Cancel</button>
            <button onClick={async () => { if (!isReady) { toast.error("Complete or override the required checks first."); return; } await persist(true); nav(`/live/stage/${sessionId}`); }} disabled={!isReady} data-testid="greenroom-studio" className="flex items-center gap-1.5 border border-taven-gold text-taven-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gold hover:text-black disabled:opacity-40"><Clapperboard className="w-4 h-4" /> Studio Stage</button>
            <button onClick={startLive} disabled={!isReady || starting} data-testid="greenroom-go-live" className="flex items-center gap-1.5 bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">{starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Go Live</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
          {/* Left: preview + devices */}
          <div className="space-y-4">
            <div className="relative bg-black aspect-video overflow-hidden border border-white/10" data-testid="greenroom-preview">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ filter: fx.blur ? "blur(6px)" : "none", transform: "scaleX(-1)" }} />
              {!camOk && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/50"><Camera className="w-10 h-10" /><p className="text-sm">Camera preview unavailable</p></div>}
              {lighting && camOk && <span data-testid="lighting-badge" className={`absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 ${lighting === "good" ? "bg-green-600" : "bg-amber-500 text-black"}`}><Sun className="w-3 h-3" /> {lighting === "good" ? "Lighting OK" : lighting === "low" ? "Too dark" : "Too bright"}</span>}
              {quality && <span className="absolute bottom-2 right-2 text-[10px] text-white/70 bg-black/60 px-2 py-0.5" data-testid="video-quality">{quality}</span>}
            </div>

            {/* mic meter */}
            <div className="border border-white/10 p-3">
              <div className="flex items-center justify-between mb-1.5"><span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-taven-gold"><Mic className="w-4 h-4" /> Microphone</span><span className="text-[11px] text-white/50">{micOk ? "Live" : "No input"}</span></div>
              <div className="h-2.5 bg-white/10 rounded overflow-hidden"><div className="h-full bg-gradient-to-r from-green-400 to-taven-gold transition-all" style={{ width: `${micLevel}%` }} data-testid="mic-meter" /></div>
            </div>

            {/* device selectors */}
            <div className="grid sm:grid-cols-3 gap-3">
              {[["cam", "Camera", Camera, devices.cams], ["mic", "Mic", Mic, devices.mics], ["spk", "Speaker", Volume2, devices.spks]].map(([k, label, Icon, list]) => (
                <div key={k}>
                  <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/60 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</label>
                  <select value={sel[k]} onChange={(e) => setSel((s) => ({ ...s, [k]: e.target.value }))} data-testid={`device-${k}`} className="w-full bg-white/5 border border-white/15 px-2 py-2 text-xs outline-none focus:border-taven-gold">
                    <option value="">Default</option>
                    {list.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${label} ${d.deviceId.slice(0, 4)}`}</option>)}
                  </select>
                  {k === "spk" && <button onClick={testSpeaker} data-testid="test-speaker" className="mt-1 text-[10px] font-bold uppercase text-taven-gold hover:underline">Test sound</button>}
                </div>
              ))}
            </div>

            {/* enhancements */}
            <div className="grid grid-cols-3 gap-2">
              {[["blur", "Background Blur", Sparkles], ["noise", "Noise Suppress", Mic], ["echo", "Echo Cancel", Volume2]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setFx((f) => ({ ...f, [k]: !f[k] }))} data-testid={`fx-${k}`} className={`flex flex-col items-center gap-1 py-2.5 border text-[10px] font-bold uppercase ${fx[k] ? "border-taven-gold bg-taven-gold/15 text-taven-gold" : "border-white/15 text-white/60 hover:border-white/40"}`}><Icon className="w-4 h-4" />{label}</button>
              ))}
            </div>
          </div>

          {/* Right: checklist + content + roster */}
          <div className="space-y-5">
            {/* readiness checklist */}
            <div className="border border-white/10 p-4" data-testid="readiness-checklist">
              <p className="text-xs font-bold uppercase tracking-wider text-taven-gold mb-1">Live Readiness</p>
              <Check ok={readiness.camera} label="Camera ready" detail={quality} onOverride={() => toggleOverride("camera")} overridden={overrides.includes("camera")} testid="check-camera" />
              <Check ok={readiness.microphone} label="Microphone ready" onOverride={() => toggleOverride("microphone")} overridden={overrides.includes("microphone")} testid="check-microphone" />
              <Check ok={readiness.network} warn={net.status === "testing"} label="Network connection" detail={net.ms != null ? `${netLabel} · ${net.ms}ms` : netLabel} onOverride={() => toggleOverride("network")} overridden={overrides.includes("network")} testid="check-network" />
              <Check ok={readiness.title} label="Stream title set" onOverride={() => toggleOverride("title")} overridden={overrides.includes("title")} testid="check-title" />
              <p className={`mt-3 text-xs font-bold ${isReady ? "text-green-400" : "text-amber-400"}`} data-testid="readiness-status">{isReady ? "All checks passed — you're ready to go live." : "Complete or override the required checks to enable Go Live."}</p>
            </div>

            {/* stream details */}
            <div className="border border-white/10 p-4 space-y-3" data-testid="stream-details">
              <p className="text-xs font-bold uppercase tracking-wider text-taven-gold">Stream Details</p>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Stream title" data-testid="gr-title" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" rows={2} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="gr-category" className="bg-white/5 border border-white/15 px-2 py-2 text-xs"><option value="">Category…</option>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                <select value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))} data-testid="gr-language" className="bg-white/5 border border-white/15 px-2 py-2 text-xs">{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </div>
              <input value={form.cover_image} onChange={(e) => setForm((f) => ({ ...f, cover_image: e.target.value }))} placeholder="Thumbnail image URL (optional)" data-testid="gr-thumbnail" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <span className="flex items-center gap-1.5 text-[11px] text-white/40"><Save className="w-3 h-3" /> Auto-saving as you type</span>
            </div>

            {/* product queue */}
            <div className="border border-white/10 p-4" data-testid="product-queue">
              <div className="flex items-center justify-between mb-2"><p className="text-xs font-bold uppercase tracking-wider text-taven-gold flex items-center gap-1.5"><ShoppingBag className="w-4 h-4" /> Product Queue ({products.length})</p><button onClick={openPicker} data-testid="gr-add-product" className="flex items-center gap-1 text-[10px] font-bold uppercase text-taven-gold hover:underline"><Plus className="w-3 h-3" /> Add</button></div>
              {products.length === 0 ? <p className="text-xs text-white/40">Add TAVEN products to feature during your live.</p> : (
                <div className="space-y-2">{products.map((p, i) => (
                  <div key={p.handle} className="flex items-center gap-2 border border-white/10 p-2" data-testid={`queue-item-${p.handle}`}>
                    {p.image && <img src={p.image} alt="" className="w-9 h-9 object-cover" />}
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold line-clamp-1">{p.title}</p><p className="text-[11px] text-taven-orange font-bold">{money(p.price)}</p></div>
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-white/40 hover:text-white disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === products.length - 1} className="text-white/40 hover:text-white disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeProduct(p.handle)} data-testid={`queue-remove-${p.handle}`} className="text-white/40 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}</div>
              )}
              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/30" data-testid="auction-queue-placeholder"><Gavel className="w-3.5 h-3.5" /> Auction queue — coming in Phase 5</div>
            </div>

            {/* roster */}
            <RosterBox roster={roster} onAdd={addRoster} onRemove={removeRoster} />
          </div>
        </div>
      </div>

      {showPicker && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" onClick={() => setShowPicker(false)}>
          <div className="bg-white text-black max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="gr-product-picker">
            <div className="flex items-center justify-between mb-3"><h3 className="font-black font-display">Add TAVEN Products</h3><button onClick={() => setShowPicker(false)}><X className="w-5 h-5" /></button></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {catalog.map((p) => { const inq = products.some((s) => s.handle === p.handle); return (
                <button key={p.handle} onClick={() => addProduct(p.handle)} disabled={inq} data-testid={`gr-pick-${p.handle}`} className={`text-left border p-2 ${inq ? "border-taven-gold opacity-60" : "border-black/10 hover:border-taven-gold"}`}>
                  {p.image && <img src={p.image} alt="" className="w-full aspect-square object-cover mb-2" />}
                  <p className="text-xs font-semibold line-clamp-2">{p.title}</p><p className="text-xs text-taven-orange font-bold">{money(p.price)}</p>
                </button>
              ); })}
              {catalog.length === 0 && <p className="col-span-full text-sm text-muted-foreground py-6 text-center">Loading…</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RosterBox = ({ roster, onAdd, onRemove }) => {
  const [inp, setInp] = useState({ guest: "", moderator: "", cohost: "" });
  const rows = [["cohost", "Co-hosts", ShieldCheck, roster.cohosts], ["moderator", "Moderators", Users, roster.moderators], ["guest", "Guests (waiting room)", UserPlus, roster.guests]];
  return (
    <div className="border border-white/10 p-4 space-y-4" data-testid="roster-box">
      <p className="text-xs font-bold uppercase tracking-wider text-taven-gold">Team & Guests</p>
      {rows.map(([role, label, Icon, list]) => (
        <div key={role} data-testid={`roster-${role}`}>
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-white/60 mb-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
          <div className="flex gap-2 mb-1.5">
            <input value={inp[role]} onChange={(e) => setInp((s) => ({ ...s, [role]: e.target.value }))} type="email" placeholder="email@…" data-testid={`roster-input-${role}`} className="flex-1 bg-white/5 border border-white/15 px-2 py-1.5 text-xs outline-none focus:border-taven-gold" />
            <button onClick={() => { onAdd(role, inp[role].trim().toLowerCase()); setInp((s) => ({ ...s, [role]: "" })); }} data-testid={`roster-add-${role}`} className="bg-taven-gold text-black px-3 text-[10px] font-bold uppercase">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5">{(list || []).map((e) => (
            <span key={e} className="flex items-center gap-1 bg-white/10 px-2 py-1 text-[11px]">{e}<button onClick={() => onRemove(role, e)} className="text-white/40 hover:text-red-400"><X className="w-3 h-3" /></button></span>
          ))}</div>
        </div>
      ))}
    </div>
  );
};
